import React, { useState } from "react";
import axios from "../../utils/axiosConfig";
import { printPortraitReport } from "../../utils/printReport";
import { formatReportDate } from "../../utils/reportDate";
import "./SalesReport.css";

const toDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const today = new Date();
const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

const formatNumber = (value, decimals = 2) => Number(value || 0).toLocaleString("en-IN", {
  minimumFractionDigits: decimals,
  maximumFractionDigits: decimals
});
const formatPrice = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value).split(",").map((part) => {
    const trimmed = part.trim();
    const numericValue = Number(trimmed);
    return trimmed && Number.isFinite(numericValue) ? formatNumber(numericValue) : trimmed;
  }).filter(Boolean).join(", ");
};

const WeightSlipReport = () => {
  const [fromDate, setFromDate] = useState(toDateInput(monthStart));
  const [toDate, setToDate] = useState(toDateInput(today));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const generateReport = async () => {
    setError("");
    if (!fromDate || !toDate) {
      setError("Select both from date and to date.");
      return;
    }
    if (fromDate > toDate) {
      setError("From date cannot be after to date.");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post("/report/weight-slip", { fromDate, toDate });
      setReport(response.data);
    } catch (err) {
      setReport(null);
      setError(err.response?.data?.message || "Failed to generate weight sheet report.");
    } finally {
      setLoading(false);
    }
  };

  const company = report?.company || {};
  const rows = report?.rows || [];
  const totals = report?.totals || {};

  return (
    <div className="container-fluid mt-3 sales-report-page">
      <div className="card shadow-sm border-0 no-print">
        <div className="card-header bg-white">
          <span className="badge bg-success fs-6 px-3 py-2">Weight Sheet Report</span>
        </div>
        <div className="card-body">
          <div className="border rounded p-3 bg-light">
            <div className="row align-items-end g-3 sales-filter-row">
              <div className="col-sm-3">
                <label className="form-label fw-semibold" htmlFor="weightSlipFromDate">From Rec Date</label>
                <input id="weightSlipFromDate" type="date" className="form-control form-control-sm" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
              </div>
              <div className="col-sm-3">
                <label className="form-label fw-semibold" htmlFor="weightSlipToDate">To Rec Date</label>
                <input id="weightSlipToDate" type="date" className="form-control form-control-sm" value={toDate} onChange={(event) => setToDate(event.target.value)} />
              </div>
              <div className="col-sm-auto">
                <button type="button" className="btn btn-success btn-sm px-4" onClick={generateReport} disabled={loading}>
                  {loading ? "Generating..." : "Generate Report"}
                </button>
              </div>
              {report && (
                <div className="col-sm-auto">
                  <button type="button" className="btn btn-outline-secondary btn-sm px-4" onClick={printPortraitReport}>
                    Print / Save PDF
                  </button>
                </div>
              )}
            </div>
            {error && <div className="alert alert-danger py-2 mt-3 mb-0">{error}</div>}
          </div>
        </div>
      </div>

      {report && (
        <section className="sales-report-print portrait-report-page mt-3">
          <header className="sales-report-header">
            <h1>{company.CompanyName || "Global Metals"}</h1>
            <h2>Weight Sheet Report</h2>
          </header>

          <div className="sales-report-meta">
            <span><strong>Rec Date:</strong> {formatReportDate(report.criteria.fromDate)} to {formatReportDate(report.criteria.toDate)}</span>
            <span><strong>Generated:</strong> {formatReportDate(report.criteria.generatedAt)}</span>
          </div>

          <table className="sales-report-table">
            <thead>
              <tr>
                <th>Deal Date</th>
                <th>Deal ID</th>
                <th>SC No</th>
                <th>SC Date</th>
                <th>Supplier</th>
                <th>Buyer</th>
                <th>Materials</th>
                <th className="number">Price</th>
                <th className="number">Commission PMT</th>
                <th>Container #</th>
                <th className="number">Weight Actual</th>
                <th className="number">Total</th>
                <th className="number">Forex</th>
                <th className="number">Commission Amt</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="14" className="empty-report">No records found for the selected Rec Date range.</td></tr>
              ) : rows.map((row, index) => (
                <tr key={`${row.DealID}-${row.ContainerNo || "container"}-${index}`}>
                  <td>{formatReportDate(row.DealDate)}</td>
                  <td>{row.DealNo || row.DealID}</td>
                  <td>{row.SCNo || "-"}</td>
                  <td>{formatReportDate(row.SCDate)}</td>
                  <td>{row.ShipperName || "-"}</td>
                  <td>{row.BuyerName || "-"}</td>
                  <td>{row.Materials || "-"}</td>
                  <td className="number">{formatPrice(row.Price)}</td>
                  <td className="number">{formatNumber(row.CommissionPMT)}</td>
                  <td>{row.ContainerNo || "-"}</td>
                  <td className="number">{formatNumber(row.WeightActual, 3)}</td>
                  <td className="number">{formatNumber(row.Total)}</td>
                  <td className="number">{formatNumber(row.Forex, 4)}</td>
                  <td className="number">{formatNumber(row.CommissionAmount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="10">Total ({rows.length} containers)</td>
                <td className="number">{formatNumber(totals.weightActual, 3)}</td>
                <td className="number">{formatNumber(totals.total)}</td>
                <td />
                <td className="number">{formatNumber(totals.commissionAmount)}</td>
              </tr>
            </tfoot>
          </table>
        </section>
      )}
    </div>
  );
};

export default WeightSlipReport;
